import { Badge, Card } from '../ui';
import {
  ACTIONS,
  MODULES,
  MODULE_LABELS,
  ROLES,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  ROLE_PERMISSIONS,
  roleHasPermission,
  type Action,
  type Module,
  type Role,
} from '../../types/permissions';

/**
 * Etiqueta legible en español para cada acción (columnas de la matriz técnica).
 */
const ACTION_LABELS: Record<Action, string> = {
  view: 'Ver',
  create: 'Crear',
  edit: 'Editar',
  delete: 'Eliminar',
};

/**
 * Capacidades que mostramos al usuario en lenguaje simple (en vez de la matriz
 * CRUD cruda). Cada una se deriva de los permisos del rol. El objetivo es que un
 * admin entienda en segundos qué puede y qué no puede hacer un rol, sin parsear
 * una tabla módulo × acción.
 *
 * `check` recibe el rol y devuelve si la capacidad está habilitada.
 */
interface Capability {
  label: string;
  check: (role: Role) => boolean;
}

const CAPABILITIES: Capability[] = [
  { label: 'Ver animales', check: (r) => roleHasPermission(r, 'animals', 'view') },
  {
    label: 'Registrar y editar animales',
    check: (r) => roleHasPermission(r, 'animals_history', 'create'),
  },
  {
    label: 'Borrar animales',
    check: (r) => roleHasPermission(r, 'animals_history', 'delete'),
  },
  {
    label: 'Ver histórico y sesiones de carga',
    check: (r) => roleHasPermission(r, 'animals_history', 'view'),
  },
  {
    label: 'Anular registros y revertir sesiones',
    check: (r) => roleHasPermission(r, 'animals_history', 'delete'),
  },
  {
    label: 'Cargar gastos, ingresos y stock',
    check: (r) => roleHasPermission(r, 'financial', 'create'),
  },
  {
    label: 'Gestionar lotes y campos',
    check: (r) => roleHasPermission(r, 'locations', 'create'),
  },
  {
    label: 'Ver reportes y estadísticas',
    check: (r) => roleHasPermission(r, 'reports', 'view'),
  },
  {
    label: 'Herramientas (notas, recordatorios)',
    check: (r) => roleHasPermission(r, 'tools', 'create'),
  },
  {
    label: 'Gestionar usuarios y configuración',
    check: (r) => roleHasPermission(r, 'admin', 'view'),
  },
];

/**
 * Lista de capacidades de un rol como chips ✓ (verde) / ✕ (apagado).
 * Es la vista "simplificada" principal.
 */
const RoleCapabilities = ({ role, compact = false }: { role: Role; compact?: boolean }) => (
  <ul className={`grid gap-2 ${compact ? 'sm:grid-cols-2' : 'sm:grid-cols-2'}`}>
    {CAPABILITIES.map((cap) => {
      const ok = cap.check(role);
      return (
        <li
          key={cap.label}
          className={`flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-xs ${
            ok ? 'border-success/20 bg-success/5' : 'border-hairline bg-paper-2/40'
          }`}
        >
          <span className={`mt-px font-semibold ${ok ? 'text-success' : 'text-sage/50'}`}>
            {ok ? '✓' : '✕'}
          </span>
          <span className={ok ? 'text-ink' : 'text-sage'}>{cap.label}</span>
        </li>
      );
    })}
  </ul>
);

/**
 * Matriz CRUD técnica (módulo × acción) para un rol. Vista de detalle.
 */
const RoleMatrix = ({ role }: { role: Role }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-paper-2 text-left text-sage">
        <tr>
          <th className="px-3 py-2 font-medium">Módulo</th>
          {ACTIONS.map((a) => (
            <th key={a} className="px-3 py-2 text-center font-medium">
              {ACTION_LABELS[a]}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-hairline">
        {MODULES.map((m) => {
          const canView = roleHasPermission(role, m, 'view');
          return (
            <tr key={m} className="align-middle">
              <td className="px-3 py-2 font-medium text-ink">{MODULE_LABELS[m]}</td>
              {ACTIONS.map((a) => {
                const allowed = roleHasPermission(role, m, a);
                return (
                  <td key={a} className="px-3 py-2 text-center">
                    {!canView ? (
                      <span className="text-xs text-sage/70">Sin acceso</span>
                    ) : allowed ? (
                      <span className="font-semibold text-success">✓</span>
                    ) : (
                      <span className="text-sage/50">✕</span>
                    )}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

/**
 * Nota corta sobre el matiz de editar vs borrar animales y los soft-deletes.
 */
const SoftDeleteNote = () => (
  <div className="mt-3 rounded-md border border-hairline bg-paper-2/50 p-3 text-xs leading-relaxed text-sage">
    <p>
      <strong className="text-ink">Editar ≠ Borrar:</strong> registrar o editar un animal es distinto
      de borrarlo. Borrar un animal, anular un registro histórico o revertir una sesión son acciones{' '}
      <strong className="text-ink">destructivas</strong> (permiso «Eliminar» del historial) que solo
      tienen Dueño y Administrador; el resto ve los botones deshabilitados. Los registros históricos{' '}
      <strong className="text-ink">no se editan</strong>: se anulan y se crean uno nuevo.
    </p>
  </div>
);

interface RolePermissionsHelpProps {
  /** Rol actualmente seleccionado en el formulario de invitación. */
  selectedRole: Role;
}

/**
 * Panel de ayuda que explica qué puede hacer cada rol, en la gestión de usuarios.
 *
 * Tres capas (de lo simple a lo detallado):
 *   1. Resumen contextual del `selectedRole`: descripción + capacidades en lenguaje
 *      simple (chips ✓/✗) + nota corta. Se actualiza al cambiar el rol.
 *   2. Colapsable «Comparar todos los roles»: las mismas capacidades para cada rol.
 *   3. Colapsable «Matriz técnica»: CRUD módulo × acción, para quien necesita el detalle.
 */
const RolePermissionsHelp = ({ selectedRole }: RolePermissionsHelpProps) => {
  return (
    <div className="mt-4 space-y-4">
      {/* 1. Resumen contextual del rol elegido */}
      <Card pad="md">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h3 className="font-display text-base font-semibold text-ink">¿Qué puede hacer este rol?</h3>
          <Badge tone="forest">{ROLE_LABELS[selectedRole]}</Badge>
        </div>
        <p className="mb-3 text-sm text-sage">{ROLE_DESCRIPTIONS[selectedRole]}</p>
        <RoleCapabilities role={selectedRole} />
        <SoftDeleteNote />
      </Card>

      {/* 2. Comparar todos los roles (capacidades) */}
      <details className="rounded-lg border border-hairline bg-paper-2/40">
        <summary className="cursor-pointer select-none px-4 py-3 font-display text-sm font-semibold text-ink">
          Comparar todos los roles
        </summary>
        <div className="space-y-4 px-4 pb-4">
          {ROLES.map((r) => (
            <div key={r}>
              <div className="mb-2 flex flex-wrap items-baseline gap-2">
                <span className="font-display text-sm font-semibold text-ink">{ROLE_LABELS[r]}</span>
                <span className="text-xs text-sage">— {ROLE_DESCRIPTIONS[r]}</span>
              </div>
              <RoleCapabilities role={r} compact />
            </div>
          ))}
          <SoftDeleteNote />
        </div>
      </details>

      {/* 3. Matriz técnica (detalle módulo × acción) */}
      <details className="rounded-lg border border-hairline bg-paper-2/40">
        <summary className="cursor-pointer select-none px-4 py-3 font-display text-sm font-semibold text-sage">
          Matriz técnica detallada (módulo × acción)
        </summary>
        <div className="space-y-5 px-4 pb-4">
          {ROLES.map((r) => (
            <div key={r}>
              <div className="mb-2 font-display text-sm font-semibold text-ink">{ROLE_LABELS[r]}</div>
              <div className="overflow-hidden rounded-md border border-hairline bg-white">
                <RoleMatrix role={r} />
              </div>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
};

export default RolePermissionsHelp;

// Re-export para tests / otros consumidores que quieran consultar la matriz.
export { ROLE_PERMISSIONS, type Role, type Module, type Action };
